import { getNewSystemNotice, getUnreadCount } from '@/api/message'
import { getToken } from '@/utils/cookieUtil'

// 两次网络刷新的最小间隔（毫秒）。
// 导航栏在每次路由切换时都会尝试刷新，不限流的话点几下就是一串重复请求。
const REFRESH_INTERVAL = 5000

// 通知类未读的字段（不含私信，私信走 /v1/chat/unread/count）
const NOTICE_KEYS = ['system', 'comment', 'follow', 'like', 'collect']

function sumNotice (data) {
  if (!data || typeof data !== 'object') return 0
  return NOTICE_KEYS.reduce((sum, key) => sum + (Number(data[key]) || 0), 0)
}

/**
 * 消息未读状态。
 *
 * 导航栏的「消息」小红点需要在任意页面都能反映未读情况，而未读数分散在两个接口：
 *   - 私信未读：GET /v1/chat/unread/count（已排除免打扰会话）
 *   - 通知未读：GET /v1/im/getNewSystemNotice（系统/评论/关注/点赞/收藏，不含私信）
 * 两者互不重叠，相加即为总未读。
 *
 * WebSocket 只在消息中心页面建立（src/view/notice/index.vue），其它页面收不到实时推送，
 * 所以红点靠「路由切换时刷新 + 消息中心把已知数值直接写回」维持，不依赖长连接。
 */
const messageStore = {
  namespaced: true,
  state: {
    // 私信未读总数
    chatUnread: 0,
    // 通知类未读总数
    noticeUnread: 0,
    // 最近一次成功刷新的时间戳，用于限流
    lastRefreshAt: 0,
    // 是否有请求在飞，避免并发重复请求
    refreshing: false
  },
  mutations: {
    setChatUnread (state, value) {
      state.chatUnread = Math.max(0, Number(value) || 0)
    },
    setNoticeUnread (state, value) {
      state.noticeUnread = Math.max(0, Number(value) || 0)
    },
    setLastRefreshAt (state, value) {
      state.lastRefreshAt = value
    },
    setRefreshing (state, value) {
      state.refreshing = value
    },
    /**
     * 本地增减私信未读（收到 WebSocket 推送 / 读完一个会话时用，省一次请求）
     */
    adjustChatUnread (state, delta) {
      state.chatUnread = Math.max(0, state.chatUnread + (Number(delta) || 0))
    },
    resetUnread (state) {
      state.chatUnread = 0
      state.noticeUnread = 0
      state.lastRefreshAt = 0
      state.refreshing = false
    }
  },
  actions: {
    /**
     * 从服务端刷新未读数
     * @param {Boolean} payload.force 忽略限流间隔（读完消息后需要立刻回落）
     */
    async refreshUnread ({ state, commit }, payload = {}) {
      // 未登录时不要发请求：这两个接口都要求登录，401 会把全局登录弹窗顶出来
      if (!getToken()) {
        commit('resetUnread')
        return
      }

      const now = Date.now()
      if (!payload.force && now - state.lastRefreshAt < REFRESH_INTERVAL) return
      if (state.refreshing) return

      commit('setRefreshing', true)
      commit('setLastRefreshAt', now)

      // 两个接口各自成败互不影响：一个挂了另一个的数值仍要生效
      const chatTask = getUnreadCount()
        .then(res => {
          if (res && res.code === 200) commit('setChatUnread', res.data)
        })
        .catch(() => {})

      const noticeTask = getNewSystemNotice()
        .then(res => {
          if (res && res.code === 200) commit('setNoticeUnread', sumNotice(res.data))
        })
        .catch(() => {})

      try {
        await Promise.all([chatTask, noticeTask])
      } finally {
        commit('setRefreshing', false)
      }
    },

    /**
     * 只刷新私信未读。消息中心自己会拉通知未读，那部分直接 commit 进来即可，
     * 不必重复请求 getNewSystemNotice。
     */
    async refreshChatUnread ({ commit }) {
      if (!getToken()) return
      try {
        const res = await getUnreadCount()
        if (res && res.code === 200) commit('setChatUnread', res.data)
      } catch (e) {
        // 静默失败：红点拉不到不影响主流程
      }
    },

    /**
     * 消息中心已经拿到的通知未读对象，直接写入，避免重复请求
     */
    applyNoticeCounts ({ commit }, counts) {
      commit('setNoticeUnread', sumNotice(counts))
    }
  },
  getters: {
    totalUnread (state) {
      return state.chatUnread + state.noticeUnread
    },
    hasUnread (state) {
      return (state.chatUnread + state.noticeUnread) > 0
    }
  }
}
export default messageStore
